/**
 * Property Check service — wraps the /api/property-checks endpoints.
 *
 * All calls go through the shared `api` axios instance (withCredentials:true)
 * per the CLAUDE.md auth pattern. Returns the unwrapped data payload from
 * the standard `{ success, data }` envelope so callers don't have to dig.
 */

import api from './api';

const unwrap = (response) => response?.data?.data ?? {};

const propertyCheckService = {
  /**
   * GET /api/property-checks/packages
   * Returns: { packages: [...] }
   */
  async listPackages() {
    const res = await api.get('/property-checks/packages');
    return unwrap(res).packages || [];
  },

  /**
   * POST /api/property-checks/:id/pay — pay-on-report bookings: starts a
   * Stripe Checkout for the fee; paying opens the report.
   * Returns: { checkoutUrl, sessionId, amount }
   */
  async pay(id) {
    const res = await api.post(`/property-checks/${id}/pay`, {
      successUrl: `${window.location.origin}/property-check/payment-success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/property-check/c/${id}?cancelled=1`,
    });
    return unwrap(res);
  },

  async listAdminPackages() {
    const res = await api.get('/property-checks/admin/packages');
    return unwrap(res).packages || [];
  },

  async updateAdminPackage(slug, payload) {
    const res = await api.patch(`/property-checks/admin/packages/${slug}`, payload);
    return unwrap(res).package;
  },

  /**
   * GET /api/property-checks/verifier-availability?city=Harare
   * Returns: { city, verifierCount, canFulfillUrgent }
   */
  async checkVerifierAvailability(city, packageSlug = null) {
    const res = await api.get('/property-checks/verifier-availability', {
      params: {
        ...(city ? { city } : {}),
        ...(packageSlug ? { packageSlug } : {}),
      },
    });
    return unwrap(res);
  },

  /**
   * POST /api/property-checks
   *
   * @param {Object} payload
   * @param {string} payload.packageSlug
   * @param {Object} payload.location
   * @param {string} [payload.ownerPropertyId]
   * @param {Object} [payload.propertyAccess]
   * @param {string[]} [payload.focusAreas]
   * @param {Object} [payload.scheduling] — { requestedDate, preferredStartTime, preferredEndTime, flexibility }
   * @param {string} payload.paymentMethod — 'stripe_checkout' | 'manual_test'
   * @param {string} [payload.successUrl]
   * @param {string} [payload.cancelUrl]
   * @returns {{ propertyCheck, checkoutUrl }}
   */
  async create(payload) {
    const res = await api.post('/property-checks', payload);
    return unwrap(res);
  },

  async listPreviousVerifiers() {
    const res = await api.get('/hires/verifier-history');
    return res?.data?.data || [];
  },

  async listAvailableVerifiers(params = {}) {
    const res = await api.get('/verifiers', {
      params: {
        limit: 20,
        isAvailable: true,
        ...params,
      },
    });
    return res?.data?.data || res?.data?.verifiers || [];
  },

  async confirmStripeCheckout(input) {
    const payload = typeof input === 'string' ? { sessionId: input } : input;
    const res = await api.post('/property-checks/stripe-checkout/confirm', payload);
    return unwrap(res);
  },

  /**
   * GET /api/property-checks/mine
   * Returns: { propertyChecks: [...] }
   */
  async listMine() {
    const res = await api.get('/property-checks/mine');
    return unwrap(res).propertyChecks || [];
  },

  /**
   * GET /api/property-checks/:id
   * Returns: { propertyCheck }
   */
  async get(id) {
    const res = await api.get(`/property-checks/${id}`);
    return unwrap(res).propertyCheck;
  },

  /**
   * POST /api/property-checks/:id/cancel
   * Returns: { cancelled, refund: { percentage, amount, status } }
   */
  async cancel(id) {
    const res = await api.post(`/property-checks/${id}/cancel`);
    return unwrap(res);
  },

  reportUrl(id) {
    const baseURL = api.defaults.baseURL || '';
    return `${baseURL}/property-checks/${id}/report.pdf`;
  },

  /**
   * Same report, but served with Content-Disposition: inline so it renders
   * in-browser (e.g. inside an iframe) instead of downloading.
   */
  reportViewUrl(id) {
    const baseURL = api.defaults.baseURL || '';
    return `${baseURL}/property-checks/${id}/report.pdf?disposition=inline`;
  },

  /**
   * Verifier-side: list this verifier's pending Property Check offers.
   * Pass { includeRecent: true } to also include the last 20 resolved offers.
   */
  async listMyOffers({ includeRecent = false } = {}) {
    const res = await api.get('/property-checks/offers/mine', {
      params: includeRecent ? { include: 'recent' } : {},
    });
    return unwrap(res);
  },

  /**
   * Verifier-side: accept an outstanding offer.
   */
  async acceptOffer(offerId) {
    const res = await api.post(`/property-checks/offers/${offerId}/accept`);
    return unwrap(res);
  },

  /**
   * Verifier-side: decline an outstanding offer.
   */
  async declineOffer(offerId) {
    const res = await api.post(`/property-checks/offers/${offerId}/decline`);
    return unwrap(res);
  },

  /**
   * POST /api/auth/lightweight-signup
   * Used inside the booking wizard when the user isn't authed yet.
   *
   * @param {Object} payload — { email, password, firstName, lastName, phoneNumber, countryCode? }
   * @returns {{ user, token, session }}
   */
  async lightweightSignup(payload) {
    const res = await api.post('/auth/lightweight-signup', payload);
    return unwrap(res);
  },
};

export default propertyCheckService;
